import { useParams } from 'react-router';
import { useIntl } from 'react-intl';
import { notifications } from '@mantine/notifications';
import {
  Container,
  Title,
  Button,
  TextInput,
  Textarea,
  Paper,
  Stack,
  Center,
  Loader,
  Text,
  Modal,
  Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { IconTrash } from '@tabler/icons-react';
import { useAppDispatch, useAppSelector } from '@store';
import { updateSettings, fetchSettings } from '@store/slices/settingsSlice';
import { getToken } from '@/commons/storageApi';
import { DigiStorageAccount } from './DigiStorageAccount';

export const UserSettings = () => {
  const { username } = useParams();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { settings, isLoading, hasFetched } = useAppSelector(
    (state) => state.settings
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [isRemoveTokenModalOpen, setIsRemoveTokenModalOpen] = useState(false);
  const [isRemovingToken, setIsRemovingToken] = useState(false);

  const tokenModalForm = useForm({
    initialValues: {
      email: '',
      password: '',
    },
  });

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      defaultEmail: '',
    },
  });

  // Update form when settings are loaded from store
  useEffect(() => {
    if (settings && hasFetched) {
      form.setFieldValue('defaultEmail', settings.defaultEmail || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, hasFetched]);

  const handleSubmit = async (data: any) => {
    if (!username) return;

    try {
      await dispatch(
        updateSettings({
          username,
          settingsData: {
            defaultEmail: data.defaultEmail || '',
          },
        })
      ).unwrap();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'settingsUpdated' }),
      });
    } catch (err: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
      });
    }
  };

  const handleGenerateToken = async (values: {
    email: string;
    password: string;
  }) => {
    if (!username) return;

    try {
      setIsGeneratingToken(true);

      // Generate token using storageApi
      const token = await getToken(values.email, values.password, {
        failedToAuthenticate: intl.formatMessage({
          id: 'digiStorageFailedToAuthenticate',
        }),
        invalidResponseTokenNotFound: intl.formatMessage({
          id: 'digiStorageInvalidResponseTokenNotFound',
        }),
        networkError: intl.formatMessage({ id: 'digiStorageNetworkError' }),
      });

      // Update settings with email and token
      await dispatch(
        updateSettings({
          username,
          settingsData: {
            digiStorageEmail: values.email,
            digiStoragePassword: values.password,
            digiStorageToken: token,
          },
        })
      ).unwrap();

      // Refresh settings to get updated values
      await dispatch(fetchSettings(username));

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'settingsUpdated' }),
      });

      setIsModalOpen(false);
      tokenModalForm.reset();
    } catch (err: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
      });
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleRemoveToken = async () => {
    if (!username) return;

    try {
      setIsRemovingToken(true);

      await dispatch(
        updateSettings({
          username,
          settingsData: {
            digiStorageToken: '',
            digiStorageEmail: '',
            digiStoragePassword: '',
          },
        })
      ).unwrap();

      // Refresh settings to get updated values
      await dispatch(fetchSettings(username));

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage({ id: 'settingsUpdated' }),
      });

      setIsRemoveTokenModalOpen(false);
    } catch (err: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
      });
    } finally {
      setIsRemovingToken(false);
    }
  };

  if (isLoading && !settings) {
    return (
      <Container size="xl">
        <Center h={250}>
          <Loader />
        </Center>
      </Container>
    );
  }

  return (
    <Container my="md" size="xl">
      <Title order={4} mb="md">
        {intl.formatMessage({ id: 'settings' })}
      </Title>

      <Stack gap="xs">
        <Paper p="md" withBorder>
          <Group gap="md" align="center" justify="space-between">
            <DigiStorageAccount
              email={settings?.digiStorageEmail}
              hasToken={!!settings?.digiStorageToken}
              onGenerateToken={() => setIsModalOpen(true)}
            />
            {settings?.digiStorageToken && (
              <Button
                color="red"
                size="xs"
                variant="light"
                leftSection={<IconTrash size={14} />}
                onClick={() => setIsRemoveTokenModalOpen(true)}
              >
                {intl.formatMessage({ id: 'delete' })}
              </Button>
            )}
          </Group>
        </Paper>

        <Paper p="md" withBorder>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <Textarea
                name="defaultEmail"
                label={intl.formatMessage({ id: 'defaultEmail' })}
                placeholder={intl.formatMessage({ id: 'defaultEmail' })}
                rows={10}
                key={form.key('defaultEmail')}
                {...form.getInputProps('defaultEmail')}
              />

              <div>
                <Button type="submit">
                  {intl.formatMessage({ id: 'save' })}
                </Button>
              </div>
            </Stack>
          </form>
        </Paper>
      </Stack>

      <Modal
        opened={isModalOpen}
        onClose={() => !isGeneratingToken && setIsModalOpen(false)}
        title={intl.formatMessage({ id: 'authenticateWithDigiStorage' })}
        centered
        closeOnClickOutside={!isGeneratingToken}
        closeOnEscape={!isGeneratingToken}
        withCloseButton={!isGeneratingToken}
      >
        <form onSubmit={tokenModalForm.onSubmit(handleGenerateToken)}>
          <Stack gap="md">
            <TextInput
              label={intl.formatMessage({ id: 'digiStorageEmail' })}
              placeholder={intl.formatMessage({ id: 'digiStorageEmail' })}
              type="email"
              required
              {...tokenModalForm.getInputProps('email')}
            />

            <TextInput
              label={intl.formatMessage({ id: 'digiStoragePassword' })}
              placeholder={intl.formatMessage({ id: 'digiStoragePassword' })}
              type="password"
              required
              {...tokenModalForm.getInputProps('password')}
            />

            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={() => setIsModalOpen(false)}
                disabled={isGeneratingToken}
              >
                {intl.formatMessage({ id: 'cancel' })}
              </Button>
              <Button
                type="submit"
                leftSection={
                  isGeneratingToken ? <Loader size="xs" /> : undefined
                }
                disabled={isGeneratingToken}
              >
                {intl.formatMessage({ id: 'authenticateWithDigiStorage' })}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={isRemoveTokenModalOpen}
        onClose={() => !isRemovingToken && setIsRemoveTokenModalOpen(false)}
        title={intl.formatMessage({ id: 'delete' })}
        centered
        closeOnClickOutside={!isRemovingToken}
        closeOnEscape={!isRemovingToken}
        withCloseButton={!isRemovingToken}
      >
        <Stack gap="md">
          <Text>
            {intl.formatMessage({ id: 'confirmDeleteDigiStorageToken' })}
          </Text>

          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => setIsRemoveTokenModalOpen(false)}
              disabled={isRemovingToken}
            >
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              color="red"
              onClick={handleRemoveToken}
              leftSection={
                isRemovingToken ? <Loader size="xs" /> : <IconTrash size={14} />
              }
              disabled={isRemovingToken}
            >
              {intl.formatMessage({ id: 'delete' })}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};
